/**
 * ModalController is a class responsible for managing the visibility and behavior of a modal dialog.
 * It integrates with various services to handle data, logging, and translation.
 *
 * @class ModalController
 * @param {Object} TaskDataService - Service for handling task data.
 * @param {Object} LogBridgeService - Service for logging operations.
 * @param {Object} $scope - AngularJS scope object.
 * @param {Object} $element - AngularJS element object.
 * @param {Object} $signals - Service for emitting and listening to signals.
 * @param {Object} $bridges - Service for various bridge operations.
 * @param {Object} $translate - Service for translation.
 * @param {Object} $timeout - Service for executing functions after a timeout.
 */
class ModalController {
    constructor(TaskDataService, LogBridgeService, $scope, $element, $signals, $bridges, $translate, $timeout) {
        // Controller meta setup
        $scope._logIdentifier = 'ModalController';
        this.$logger = $bridges.logger();

        $scope.isModalVisible = false;
        $scope.cancelText = $translate.instant('BTN.CANCEL');
        $scope.config = {};
        $scope.buttonCaptions = [];

        // Bind methods to the class instance
        $scope.closeModal = this.closeModal.bind(this);
        $scope.onCustomButtonClick = this.onCustomButtonClick.bind(this);
        $scope.onCancelClick = this.onCancelClick.bind(this);

        this.$scope = $scope;
        this.$signals = $signals;
        this.$timeout = $timeout;
        this._onShowModal = this.onShowModal.bind(this);

        // Event handler
        $signals.on(IB.Signals.SHOW_MODAL, this._onShowModal);
        $scope.$on('$destroy', () => {
            this.$signals.remove(IB.Signals.SHOW_MODAL, this._onShowModal);
        });
    }

    /**
     * Closes the modal dialog and emits a signal indicating the modal has closed.
     */
    closeModal() {
        this.$timeout(() => {
            this.$scope.isModalVisible = false;
            this.$signals.emit(IB.Signals.MODAL_DID_CLOSE, this.$scope.config);
        });
    }

    /**
     * Handles the click event for custom buttons within the modal.
     * Logs the event and executes the associated callback if it exists.
     *
     * @param {string} caption - The caption of the button clicked.
     */
    onCustomButtonClick(caption) {
        this.$logger.info(this.$scope, 'CUSTOM BUTTON CLICKED', caption);

        var callback = this.$scope.config.buttons[caption];

        if (typeof callback === 'function') {
            callback();
        }

        this.closeModal();
    }

    /**
     * Handles the cancel click event, logging the event and closing the modal.
     */
    onCancelClick() {
        this.$logger.info(this.$scope, 'CLOSE IT');
        this.closeModal();
    }

    /**
     * Handles the event to show the modal.
     * Configures the modal based on the event data and sets its visibility.
     *
     * @param {Object} evnt - The event object containing modal configuration.
     */
    onShowModal(evnt) {
        this.$logger.info(this.$scope, 'REQUIRE TO SHOW MODAL', evnt);
        this.$scope.config = evnt;

        if (typeof evnt.buttons !== 'undefined') {
            this.$scope.buttonCaptions = Object.keys(evnt.buttons);
        } else {
            this.$scope.buttonCaptions = [];
        }

        if (this.$scope.config.cancelText) {
            this.$scope.cancelText = this.$scope.config.cancelText;
        }

        this.$scope.isModalVisible = true;
    }
}

export default ModalController;
